// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';
import './AccountSetup.css';
import { initializeCheckout } from "../services/stripe/stripeService";

const LOCAL_STORAGE_KEY = 'accountSetupAnswers';

const questions = [
  { id: 'imRexi', skipInput: true },
  { id: 'udpateAccountInfo', skipInput: true },
  {
    id: 'certifierId',
    fields: [
      { key: 'companyName', prompt: 'The Certifier ID you will use in your certification data', placeholder: 'e.g., your company name' },
    ],
  },
  {
    id: 'contact1',
    fields: [
      { key: 'contactName', prompt: 'Contact name', placeholder: 'e.g., your name' },
      { key: 'contactPhone', prompt: 'Contact phone', placeholder: 'e.g., your office phone number' },
      { key: 'contactEmail', prompt: 'Contact email', placeholder: 'e.g., you@myco.com' },
    ],
  },
  {
    id: 'contact2',
    fields: [
      { key: 'contactAddressLine1', prompt: 'Contact address line 1', placeholder: '' },
      { key: 'contactAddressLine2', prompt: 'Contact address line 2', placeholder: '' },
      { key: 'contactSuiteNumber', prompt: 'Contact suite number', placeholder: '' },
      { key: 'contactCity', prompt: 'Contact city', placeholder: '' },
      { key: 'contactStateProvince', prompt: 'Contact state/province', placeholder: '' },
      { key: 'contactCountry', prompt: 'Contact country', placeholder: '' },
      { key: 'contactZipCode', prompt: 'Contact zipcode', placeholder: '' },
    ],
  },
  {
    id: 'certificateCompanyName',
    fields: [
      { key: 'certificateCompanyName', prompt: 'The company name to appear on your certificates', placeholder: 'e.g., your company name' },
    ],
  },
  {
    id: 'setupComplete',
    skipInput: true,
  },
];

const guidanceText = [
  "I'm Rexi and I'm looking forward to helping you with your Consumer Product Safety Commission (CPSC) eFiling needs! You'll see an Ask Rexi button on the menu after we complete your account setup, please be sure to use my knowledge!",
  "You'll also see a Certification Management button where you can edit this account setup information at any time.",
  "Your account setup information will provide some of the data that must go into the certificate data set for the products you certify. Starting with your Certifier ID, this is simply how you will identify your company in your eFiling data; it is typically just your company name.",
  "Contact information is required in certificate data. Don't enter a personal phone number or email address.",
  "Enter your company address information.",
  <>
    Just one more thing! What company name should we use at the top of your certificate PDF's?
    <p>
      Clicking Finish Setup will bring you to the checkout page and confirm you have read and agreed to our{" "}
      <a
        href="https://www.comverex.com/terms_and_conditions.php"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-link"
      >
        Terms & Conditions
      </a>{" "}
      and{" "}
      <a
        href="https://www.comverex.com/privacy_policy.php"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-link"
      >
        Privacy Policy
      </a>
      .
    </p>
    <p>
      Your Comverex subscription is <strong>$95/month</strong>, and each test report you upload for
      analysis is charged a <strong>$2 processing fee</strong>.
    </p>
    <p>
      You can cancel your subscription at any time.
    </p>
  </>,
  "Your account is set up! Remember to Ask Rexi for guidance as you prepare for CPSC eFiling!",
];

const AccountSetup = () => {
  const navigate = useNavigate();
  const { user, setUser, setProfile } = useAuth();

  // 🧭 Redirect logic for users who already completed setup
  React.useEffect(() => {
    const checkStatus = async () => {
      if (!user?.id) return;

        // 🧩 Skip auto-redirect when returning from Stripe
        if (window.location.search.includes('status=')) return;

      const { data, error } = await supabase
        .from('users')
        .select('status, account_payment_complete')
        .eq('auth_id', user.id)
        .single();

      if (!error && data) {
      // Grant access only if Stripe payment confirmed or status is active
        if (data.status === 'active' || data.account_payment_complete) {
          navigate('/dashboard/testreports', { replace: true });
        }
      }
    };

    checkStatus();
  }, [user, navigate]);

  // Read query params like ?step=6&status=success
  const locationHook = useLocation();
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(locationHook.search);
    const stepParam = params.get("step");
    const statusParam = params.get("status");

    if (stepParam && !isNaN(Number(stepParam))) {
      setCurrentStep(Number(stepParam));
    }
    if (statusParam) {
      setPaymentStatus(statusParam);
    }
  }, [locationHook.search]);

  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        setAnswers(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved answers:', e);
      }
    }
  }, []);

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmitSetup = async () => {
  
    // 🧩 Step 1 — upsert certification management  
    const { error: upsertError } = await supabase
      .from('certification_management')
      .upsert({
        auth_id: user.id,
        certifier_id: answers.companyName,
        company_name_on_certificates: answers.certificateCompanyName,
        contact_name: answers.contactName,
        contact_address_line_1: answers.contactAddressLine1,
        contact_address_line_2: answers.contactAddressLine2,
        contact_suite: answers.contactSuiteNumber,
        contact_city: answers.contactCity,
        contact_state_province: answers.contactStateProvince,
        contact_country: answers.contactCountry,
        contact_postal_code: answers.contactZipCode,
        contact_telephone: answers.contactPhone,
        contact_email: answers.contactEmail,
        status: 'active',
        updated_at: new Date().toISOString(),
      }, { onConflict: 'auth_id' });

    if (upsertError) {
      console.error('❌ certification_management upsert failed:', upsertError.message);
      return;
    }

    // 🧩 Step 2 — mark setup complete
    const { error: updateError } = await supabase
      .from('users')
      .update({ account_setup_complete: true })
      .eq('auth_id', user.id);

    if (updateError) {
      console.error('❌ users table update failed:', updateError.message);
      return;
    }

    // 🧩 Step 3 — clean up and go to checkout
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    // setCurrentStep((prev) => prev + 1);
// console.log("🔑 Stripe publishable key in frontend:", process.env.REACT_APP_STRIPE_PUBLIC_KEY);    
//    await initializeCheckout("price_1SJihaEMB3tp6STE9wMPxfUZ");
    console.log("🔑 Stripe publishable key in frontend:", process.env.REACT_APP_STRIPE_PUBLIC_KEY);
    await initializeCheckout(""); // server reads prices from .env
  };

  const question = questions[currentStep];

  return (
    <div className="account-setup-container">
      <div className="rexi-header">
        <img src="/rexi-logo.png" alt="Rexi" className="rexi-header-logo" />
        <div className="rexi-header-text">{guidanceText[currentStep] || ''}</div>
      </div>

      {(question.skipInput || question.fields) && (
        <div className="input-section">
          {question.fields?.map(field => (
            <div key={field.key} style={{ marginBottom: '16px' }}>
              <label htmlFor={field.key} className="input-label">{field.prompt}</label>
              <input
                id={field.key}
                type="text"
                placeholder={field.placeholder}
                value={answers[field.key] || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  const key = field.key;
                  setAnswers(prev => {
                    const updated = { ...prev, [key]: value };
                    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
                    return updated;
                  });
                }}
                className="input-field"
              />
            </div>
          ))}
        </div>
      )}

      <div className="nav-buttons">
        {currentStep === questions.length - 1 ? (
          // Final step - only Done button
          <button onClick={async () => {
            // Update user state after they've seen the success message
            if (setUser) {
              setUser((prev) => ({ ...prev, isSetupComplete: true }));
            }
            
            const { data: refetchData, error: fetchError } = await supabase
              .from('users')
              .select('account_setup_complete')
              .eq('auth_id', user.id)
              .single();

            if (!fetchError && setProfile) {
              setProfile({ accountSetupComplete: refetchData?.account_setup_complete ?? false });
            }

            navigate('/dashboard/testreports');
          }} className="nav-button primary" style={{ margin: '0 auto' }}>
            Done
          </button>
        ) : (
          <>
            <button onClick={handleBack} disabled={currentStep === 0} className="nav-button secondary">
              Back
            </button>

            {currentStep < questions.length - 2 ? (
              <button onClick={handleNext} className="nav-button primary">
                Next
              </button>
            ) : (
              <button onClick={handleSubmitSetup} className="nav-button primary">
                Finish Setup
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AccountSetup;
